/**
 * Grid / list toggle for the header search dropdown.
 *
 * The choice is written to `data-pb-view` on the surrounding `<predictive-search-component>` rather
 * than on the results list itself. That placement is load-bearing: `assets/predictive-search.js`
 * morphs the whole of `ref="predictiveSearchResults"` on every keystroke, so an attribute set on
 * anything inside it would be discarded the next time the shopper typed a character. The component
 * element is never replaced, and CSS reaches down into the fresh markup from there.
 *
 * The preference persists in `localStorage`, so a shopper who prefers tiles gets tiles the next time
 * they open the modal. Storage can throw — Safari private browsing, cookies blocked — so every
 * access is guarded and falls back to the default rather than leaving the toggle dead.
 *
 * That default is the list. `search-modal.liquid` hardcodes the same value in `data-pb-view` and in
 * the buttons' `aria-pressed`, so the markup is already correct before this module runs and there
 * is no flash of the other view; the two must be changed together.
 *
 * No re-fetch and no re-render: both views are the same markup under different CSS. Switching is a
 * single attribute write, which also means it cannot race the in-flight search request.
 */

const STORAGE_KEY = 'pb-search-view';
const VIEWS = ['grid', 'list'];
const DEFAULT_VIEW = 'list';

/** @returns {string} the stored view, or the default when storage is unavailable or holds junk */
function readStoredView() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && VIEWS.includes(stored) ? stored : DEFAULT_VIEW;
  } catch {
    return DEFAULT_VIEW;
  }
}

class SearchViewToggle extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('predictive-search-component');
    this.addEventListener('click', this.#handleClick);
    this.#apply(readStoredView());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    const button = target instanceof Element ? target.closest('[data-pb-view-option]') : null;
    if (!(button instanceof HTMLElement)) return;

    const view = button.dataset.pbViewOption;
    if (!view || !VIEWS.includes(view)) return;

    this.#apply(view);

    try {
      localStorage.setItem(STORAGE_KEY, view);
    } catch {
      // A shopper who cannot persist the choice should still get it for this visit.
    }
  };

  /** @param {string} view */
  #apply(view) {
    this.scope?.setAttribute('data-pb-view', view);

    for (const button of this.querySelectorAll('[data-pb-view-option]')) {
      button.setAttribute('aria-pressed', String(button.dataset.pbViewOption === view));
    }
  }
}

if (!customElements.get('pb-search-view')) {
  customElements.define('pb-search-view', SearchViewToggle);
}
